/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import * as RadixUIToggle from "@radix-ui/react-toggle";
import type { ComponentProps } from "react";
import { useEffect, useState } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  colors,
  constantSemanticColors,
  numericPixels,
  numericValues,
  radius,
  shades,
  spacing,
  stroke,
} from "../../../../../global/emotion";

const baseStyle: Interpolation = {
  borderRadius: radius.full,
  borderStyle: "solid",
  borderWidth: stroke.regular,
  boxSizing: "border-box",
  cursor: "pointer",
  transition: "padding 100ms ease-in",
};
const disabledStyle: Interpolation = {
  cursor: "not-allowed",
  opacity: numericValues["0.5"],
};

const circleBaseStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: constantSemanticColors.onColorHighest,
  borderColor: theme.semanticColors.higherContrastVeryLow,
  borderRadius: radius.full,
  borderStyle: "solid",
  borderWidth: stroke.regular,
  boxSizing: "border-box",
});

const stylesPressed: Record<`${boolean}`, Interpolation<ThemeType>> = {
  false: (theme) => ({
    backgroundColor: theme.semanticColors.neutralsLowest,
    borderColor: theme.semanticColors.higherContrastLowest,
  }),
  true: (theme) => ({
    backgroundColor: theme.semanticColors.accentMedium,
    borderColor: constantSemanticColors.onColorMedLow,
  }),
};

const stylesPressedCircle: Record<`${boolean}`, Interpolation> = {
  false: {},
  true: {
    boxShadow: `${numericValues[0]} ${numericPixels[2]} ${numericPixels[4]} ${numericValues[0]} ${shades.shade25}`,
  },
};

const stylesPressedDisabled: Record<`${boolean}`, Interpolation> = {
  false: {
    backgroundColor: colors.transparent,
  },
  true: {},
};

const stylesSizeRoot: Record<ToggleProps["size"], Interpolation> = {
  s: {
    maxWidth: "36px",
    maxHeight: "24px",
  },
  m: {
    maxWidth: "52px",
    maxHeight: "32px",
  },
  l: {
    maxWidth: "68px",
    maxHeight: "40px",
  },
};

const stylesPressedSize: Record<
  `${boolean}${Uppercase<ToggleProps["size"]>}`,
  Interpolation
> = {
  falseS: {
    paddingBottom: `calc(${spacing.xxs} - ${spacing.t})`,
    paddingLeft: `calc(${spacing.xxs} - ${spacing.t})`,
    paddingRight: `calc(${spacing.m} - ${spacing.t})`,
    paddingTop: `calc(${spacing.xxs} - ${spacing.t})`,
  },
  trueS: {
    paddingBottom: `calc(${spacing.xxs} - ${spacing.t})`,
    paddingLeft: `calc(${spacing.m} - ${spacing.t})`,
    paddingRight: `calc(${spacing.xxs} - ${spacing.t})`,
    paddingTop: `calc(${spacing.xxs} - ${spacing.t})`,
  },
  falseM: {
    paddingBottom: `calc(${spacing.xxs} - ${spacing.t})`,
    paddingLeft: `calc(${spacing.xxs} - ${spacing.t})`,
    paddingRight: `calc(${spacing.l} - ${spacing.t})`,
    paddingTop: `calc(${spacing.xxs} - ${spacing.t})`,
  },
  trueM: {
    paddingBottom: `calc(${spacing.xxs} - ${spacing.t})`,
    paddingLeft: `calc(${spacing.l} - ${spacing.t})`,
    paddingRight: `calc(${spacing.xxs} - ${spacing.t})`,
    paddingTop: `calc(${spacing.xxs} - ${spacing.t})`,
  },
  falseL: {
    paddingBottom: `calc(${spacing.xxs} - ${spacing.t})`,
    paddingLeft: `calc(${spacing.xxs} - ${spacing.t})`,
    paddingRight: `calc(${spacing.xl} - ${spacing.t})`,
    paddingTop: `calc(${spacing.xxs} - ${spacing.t})`,
  },
  trueL: {
    paddingBottom: `calc(${spacing.xxs} - ${spacing.t})`,
    paddingLeft: `calc(${spacing.xl} - ${spacing.t})`,
    paddingRight: `calc(${spacing.xxs} - ${spacing.t})`,
    paddingTop: `calc(${spacing.xxs} - ${spacing.t})`,
  },
};

const stylesSizeCircle: Record<ToggleProps["size"], Interpolation> = {
  s: {
    height: spacing.m,
    width: spacing.m,
  },
  m: {
    height: spacing.l,
    width: spacing.l,
  },
  l: {
    height: spacing.xl,
    width: spacing.xl,
  },
};

export type ToggleProps = ComponentProps<typeof RadixUIToggle.Root> & {
  size?: "s" | "m" | "l";
};

export const Toggle = ({
  disabled = false,
  pressed = false,
  size = "m",
  ...props
}: ToggleProps) => {
  const [internalState, setInternalState] = useState(pressed);

  const toggleInternalState = () => setInternalState(!internalState);

  useEffect(() => {
    setInternalState(pressed);
  }, [pressed]);

  return (
    <RadixUIToggle.Root
      disabled={disabled}
      onPressedChange={toggleInternalState}
      pressed={internalState}
      css={[
        baseStyle,
        disabled && disabledStyle,
        stylesPressed[internalState.toString() as keyof typeof stylesPressed],
        stylesPressedSize[
          `${internalState.toString()}${size.toUpperCase()}` as keyof typeof stylesPressedSize
        ],
        stylesSizeRoot[size],
        disabled && !internalState ? stylesPressedDisabled.false : null,
      ]}
      {...props}
    >
      <div
        css={[
          circleBaseStyle,
          stylesPressedCircle[
            internalState.toString() as keyof typeof stylesPressedCircle
          ],
          stylesSizeCircle[size],
        ]}
      />
    </RadixUIToggle.Root>
  );
};
